import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  listLegalCertificateLeads,
  type LegalCertificateListItem,
  type LegalCertificateStageFilter,
  type LegalCertificateSort,
} from "api/legalCertificate";
import { getLegalCertificatePathForLead } from "utils/legalCertificatePathForLead";

import iconSearch from "../../assets/svg/icon-search.svg";
import iconFilter from "../../assets/svg/icon-filter.svg";
import iconFilterSort from "../../assets/svg/icon-filter-sort.svg";
import iconCheckbox from "../../assets/svg/icon-checkbox.svg";
import iconMore from "../../assets/svg/icon-more.svg";

const PAGE_SIZE = 14;

const STAGE_OPTIONS: { value: LegalCertificateStageFilter | "ALL"; label: string }[] = [
  { value: "ALL", label: "All stages" },
  { value: "DISCUSSION", label: "Discussion" },
  { value: "FILING", label: "Filing" },
  { value: "STATUS", label: "Status" },
];

const SORT_OPTIONS: { value: LegalCertificateSort; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

function formatCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AllLegalCertificatesView() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<LegalCertificateListItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [navigatingId, setNavigatingId] = useState<string | null>(null);

  const [stageFilter, setStageFilter] = useState<
    LegalCertificateStageFilter | "ALL"
  >("ALL");
  const [sortOrder, setSortOrder] = useState<LegalCertificateSort>("newest");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const sortRef = useRef<HTMLDivElement | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, stageFilter, sortOrder]);

  useEffect(() => {
    if (!filterOpen && !sortOpen) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (filterOpen && filterRef.current && !filterRef.current.contains(t)) {
        setFilterOpen(false);
      }
      if (sortOpen && sortRef.current && !sortRef.current.contains(t)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [filterOpen, sortOpen]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listLegalCertificateLeads({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
        stage: stageFilter === "ALL" ? undefined : stageFilter,
        sort: sortOrder,
      });
      setItems(data.items);
      setTotalRecords(data.total);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load legal certificates"
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, stageFilter, sortOrder]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((r) => r.id)));
    }
  };

  const toggleSelectRow = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRowClick = async (item: LegalCertificateListItem) => {
    if (navigatingId) return;
    setNavigatingId(item.id);
    try {
      const path = await getLegalCertificatePathForLead(item.id);
      navigate(path);
    } finally {
      setNavigatingId(null);
    }
  };

  return (
    <DashboardLayout headerBrand="UpRight" headerTitle="Certificates">
      <div className="flex-1 p-2 min-h-0 overflow-auto">
        <div className="bg-white rounded-[8px] p-5 flex flex-col gap-6 h-full">
          {/* Title row */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <h1 className="text-[20px] font-semibold leading-5 text-[#222222]">
              All Certificates
            </h1>
            <div className="flex flex-row items-center gap-4 flex-wrap">
              {/* Search */}
              <div className="flex flex-row items-center gap-2 px-3 py-2 w-full sm:w-[250px] h-9 bg-[#F7F7F7] rounded-[6px]">
                <img src={iconSearch} alt="" className="w-3.5 h-3.5 shrink-0" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 min-w-0 bg-[#F7F7F7] text-xs font-normal leading-5 text-[#777777] placeholder:text-[#777777] focus:outline-none"
                />
              </div>

              {/* Filter — Stage */}
              <div ref={filterRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setFilterOpen((v) => !v);
                    setSortOpen(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 h-9 border rounded-[6px] transition-colors ${
                    stageFilter !== "ALL"
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                  aria-haspopup="listbox"
                  aria-expanded={filterOpen}
                >
                  <img src={iconFilter} alt="" className="w-3.5 h-3.5" />
                  <span className="text-xs font-normal leading-5 text-[#222222]">
                    {stageFilter === "ALL"
                      ? "Filter"
                      : `Stage: ${
                          STAGE_OPTIONS.find((o) => o.value === stageFilter)
                            ?.label
                        }`}
                  </span>
                </button>
                {filterOpen && (
                  <div
                    role="listbox"
                    className="absolute right-0 mt-1 w-44 bg-white border border-[#EEEEEE] rounded-[6px] shadow-md py-1 z-30"
                  >
                    {STAGE_OPTIONS.map((opt) => {
                      const selected = stageFilter === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setStageFilter(opt.value);
                            setFilterOpen(false);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs leading-5 transition-colors ${
                            selected
                              ? "bg-[#F7F7F7] text-[#222222] font-medium"
                              : "text-[#444444] hover:bg-[#FAFAFA]"
                          }`}
                        >
                          {opt.label}
                          {selected && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 5"
                                stroke="#222222"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sort */}
              <div ref={sortRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setSortOpen((v) => !v);
                    setFilterOpen(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 h-9 border rounded-[6px] transition-colors ${
                    sortOrder !== "newest"
                      ? "border-[#222222] bg-[#F7F7F7]"
                      : "border-[#EEEEEE] bg-white hover:bg-[#F7F7F7]"
                  }`}
                  aria-haspopup="listbox"
                  aria-expanded={sortOpen}
                >
                  <img src={iconFilterSort} alt="" className="w-3.5 h-3.5" />
                  <span className="text-xs font-normal leading-5 text-[#222222]">
                    {SORT_OPTIONS.find((o) => o.value === sortOrder)?.label ??
                      "Sort"}
                  </span>
                </button>
                {sortOpen && (
                  <div
                    role="listbox"
                    className="absolute right-0 mt-1 w-44 bg-white border border-[#EEEEEE] rounded-[6px] shadow-md py-1 z-30"
                  >
                    {SORT_OPTIONS.map((opt) => {
                      const selected = sortOrder === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setSortOrder(opt.value);
                            setSortOpen(false);
                          }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-left text-xs leading-5 transition-colors ${
                            selected
                              ? "bg-[#F7F7F7] text-[#222222] font-medium"
                              : "text-[#444444] hover:bg-[#FAFAFA]"
                          }`}
                        >
                          {opt.label}
                          {selected && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 5"
                                stroke="#222222"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Create New */}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    "/dashboard/legal-certificate/new/discussion/preliminary-discussion-with-the-client"
                  )
                }
                className="flex items-center justify-center px-4 py-2 h-9 bg-[#222222] rounded-[6px] hover:bg-[#333333] transition-colors"
              >
                <span className="text-xs font-semibold leading-5 text-white">
                  Create New
                </span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="border border-[#EEEEEE] rounded-[8px] flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full min-w-[1100px] border-collapse table-fixed">
                <colgroup>
                  <col className="w-[48px]" />
                  <col className="w-[130px]" />
                  <col className="w-[140px]" />
                  <col className="w-[150px]" />
                  <col className="w-[200px]" />
                  <col className="w-[180px]" />
                  <col className="w-[100px]" />
                  <col className="w-[160px]" />
                  <col className="w-[56px]" />
                </colgroup>
                <thead>
                  <tr className="bg-[#F7F7F7] border-b border-[#EEEEEE]">
                    <th className="sticky top-0 left-0 z-20 bg-[#F7F7F7] px-5 h-11 text-left border-r border-[#EEEEEE]">
                      <button
                        type="button"
                        onClick={toggleSelectAll}
                        disabled={loading || items.length === 0}
                        className="w-6 h-6 flex items-center justify-center disabled:opacity-40"
                        aria-label="Select all"
                      >
                        <img src={iconCheckbox} alt="" className="w-6 h-6" />
                      </button>
                    </th>
                    {[
                      "Lead No.",
                      "Mark",
                      "WhatsApp Number",
                      "Email Address",
                      "Client Name",
                      "Stage",
                      "Created On",
                    ].map((col) => (
                      <th
                        key={col}
                        className="sticky top-0 z-10 bg-[#F7F7F7] px-5 h-11 text-left text-[11px] font-semibold uppercase tracking-wider leading-4 text-[#6D6D6D]"
                      >
                        {col}
                      </th>
                    ))}
                    <th
                      className="sticky top-0 right-0 z-20 bg-[#F7F7F7] px-5 h-11"
                      aria-label="Actions"
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-5 py-16 text-center text-sm text-[#777777]"
                      >
                        Loading certificates…
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-5 py-16 text-center text-sm text-[#777777]"
                      >
                        No certificates yet. Click &quot;Create New&quot; to get
                        started.
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr
                        key={row.id}
                        className={`group hover:bg-[#FAFAFA] transition-colors cursor-pointer ${
                          navigatingId === row.id
                            ? "opacity-60 pointer-events-none"
                            : ""
                        }`}
                        onClick={() => handleRowClick(row)}
                      >
                        <td
                          className="sticky left-0 z-[1] bg-white group-hover:bg-[#FAFAFA] px-5 py-4 border-r border-[#F0F0F0] align-middle"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={(e) => toggleSelectRow(e, row.id)}
                            className="w-6 h-6 flex items-center justify-center"
                            aria-label={`Select ${row.leadNo}`}
                          >
                            <img src={iconCheckbox} alt="" className="w-6 h-6" />
                          </button>
                        </td>
                        <td className="px-5 py-4 align-middle text-xs font-medium leading-5 text-[#222222] tabular-nums truncate">
                          {row.leadNo}
                        </td>
                        <td
                          className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#222222] truncate"
                          title={row.markName ?? undefined}
                        >
                          {row.markName ?? (
                            <span className="text-[#999999]">–</span>
                          )}
                        </td>
                        <td
                          className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#444444] tabular-nums truncate"
                          title={row.phone ?? undefined}
                        >
                          {row.phone ?? <span className="text-[#999999]">–</span>}
                        </td>
                        <td
                          className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#444444] truncate"
                          title={row.email ?? undefined}
                        >
                          {row.email ?? <span className="text-[#999999]">–</span>}
                        </td>
                        <td
                          className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#222222] truncate"
                          title={row.clientName || undefined}
                        >
                          {row.clientName || (
                            <span className="text-[#999999]">–</span>
                          )}
                        </td>
                        <td className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#222222] truncate">
                          {row.stage ?? <span className="text-[#999999]">–</span>}
                        </td>
                        <td className="px-5 py-4 align-middle text-xs font-normal leading-5 text-[#444444] whitespace-nowrap">
                          {formatCreatedAt(row.createdAt)}
                        </td>
                        <td
                          className="px-5 py-4 align-middle"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="w-7 h-7 flex items-center justify-center rounded-[6px] hover:bg-[#F0F0F0] transition-colors ml-auto"
                            aria-label="More options"
                          >
                            <img src={iconMore} alt="" className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-row justify-end items-center gap-6 shrink-0">
            <span className="text-xs font-normal leading-5 text-[#6D6D6D]">
              {totalRecords === 0
                ? "No records"
                : `Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(
                    page * PAGE_SIZE,
                    totalRecords
                  )} of ${totalRecords} records`}
            </span>
            <div className="flex flex-row items-center gap-3">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex items-center justify-center w-7 h-7 border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="rotate-180"
                >
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="#222222"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="flex items-center justify-center w-7 h-7 border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="#222222"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
